/**
 * Gazeparts custom contact form.
 *
 * Keep the constants below in sync with worker/src/validation.ts on the
 * server — this file only exists to give the shopper fast, friendly
 * feedback before a round trip; the server is the actual source of truth
 * and re-validates everything independently.
 */
(function () {
  'use strict';

  var MAX_FILES = 6;
  var MAX_FILE_SIZE = 8 * 1024 * 1024; // 8 MB
  var MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB
  var ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var turnstileLoadPromise = null;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoadPromise) return turnstileLoadPromise;

    turnstileLoadPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = function () {
        resolve(window.turnstile);
      };
      script.onerror = function () {
        reject(new Error('turnstile_script_failed'));
      };
      document.head.appendChild(script);
    });

    return turnstileLoadPromise;
  }

  function extensionOf(filename) {
    var idx = filename.lastIndexOf('.');
    if (idx === -1) return '';
    return filename.slice(idx + 1).toLowerCase();
  }

  function formatSize(bytes) {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return Math.round(bytes / 1024) + ' KB';
  }

  /**
   * Reads the current product's SKU, but only when we're actually on a
   * product page — returns null everywhere else (home, collections, blog,
   * Contact Us, etc.), which is intentional: those pages have nothing
   * precise to report. Liquid's `product` object isn't reachable from a
   * static/global section like the popup, so this reads Shopify's own
   * client-side analytics data instead — `window.ShopifyAnalytics.meta` is
   * injected via {{ content_for_header }} in <head> on every Shopify theme,
   * so it's there regardless of which section triggers the form.
   *
   * If the shopper has picked a specific variant, Shopify reflects that in
   * the URL as `?variant=<id>`; match that variant's SKU when present,
   * otherwise fall back to the product's first variant.
   */
  function getProductSku() {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (!meta || !meta.page || meta.page.pageType !== 'product') return null;

      var variants = (meta.product && meta.product.variants) || [];
      if (!variants.length) return null;

      var variantId = new URLSearchParams(window.location.search).get('variant');
      var variant = null;
      if (variantId) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === variantId) {
            variant = variants[i];
            break;
          }
        }
      }
      if (!variant) variant = variants[0];

      return (variant && variant.sku) || null;
    } catch (err) {
      return null;
    }
  }

  var ERROR_MESSAGES = {
    firstName: { required: 'Please enter your first name.', too_long: 'First name is too long.' },
    email: { required: 'Please enter your email address.', invalid_format: 'Please enter a valid email address.', too_long: 'Email address is too long.' },
    message: { required: 'Please enter a message.', too_long: 'Message is too long.' },
    lastName: { too_long: 'Last name is too long.' },
    phone: { too_long: 'Phone number is too long.' },
    orderNumber: { too_long: 'Order number is too long.' },
  };

  var GENERIC_ERROR_MESSAGES = {
    captcha_failed: 'The security check failed — please try again.',
    rate_limited: 'Too many submissions — please wait a minute and try again.',
    origin_not_allowed: 'This form could not be submitted from here. Please refresh the page and try again.',
    email_send_failed: "We couldn't send your message right now. Please try again in a few minutes.",
    server_misconfigured: "We couldn't send your message right now. Please try again later.",
    too_many_files: 'Please attach at most 6 photos.',
    file_too_large: 'Each photo must be 8 MB or smaller.',
    total_too_large: 'The total size of your photos must be 20 MB or smaller.',
    unsupported_file_type: 'Photos must be JPG, PNG, WEBP, or HEIC.',
    invalid_form_data: 'Something went wrong submitting the form. Please try again.',
    not_found: 'Something went wrong submitting the form. Please try again.',
    method_not_allowed: 'Something went wrong submitting the form. Please try again.',
  };

  function GpContactForm() {
    return Reflect.construct(HTMLElement, [], GpContactForm);
  }

  GpContactForm.prototype = Object.create(HTMLElement.prototype);
  GpContactForm.prototype.constructor = GpContactForm;
  Object.setPrototypeOf(GpContactForm, HTMLElement);

  GpContactForm.prototype.connectedCallback = function () {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.statusEl = this.querySelector('[data-gp-form-status]');
    this.successEl = this.querySelector('[data-gp-success]');
    this.submitButton = this.querySelector('[data-gp-submit]');
    this.turnstileContainer = this.querySelector('[data-gp-turnstile]');
    this.fileInput = this.querySelector('[data-gp-file-input]');
    this.fileList = this.querySelector('[data-gp-file-list]');
    this.submitting = false;
    this.turnstileWidgetId = null;

    var pageUrlField = this.querySelector('[data-gp-page-url]');
    if (pageUrlField) pageUrlField.value = window.location.href;

    var skuField = this.querySelector('[data-gp-sku]');
    if (skuField) skuField.value = getProductSku() || '';

    this._selectedFiles = [];

    this._onSubmit = this.handleSubmit.bind(this);
    this.form.addEventListener('submit', this._onSubmit);

    if (this.fileInput) {
      this._onFileChange = this.handleFileChange.bind(this);
      this.fileInput.addEventListener('change', this._onFileChange);
    }

    // The popup version of this form starts out inside an overlay that has
    // the `hidden` attribute (see gp-contact-form-popup.js), so on page load
    // this custom element connects while still display:none. Rendering the
    // Turnstile widget's cross-origin iframe into a hidden container is
    // unreliable in some browsers — notably desktop Safari with "Prevent
    // cross-site tracking" on (the default) — where the challenge silently
    // never completes, leaving the response token empty and every
    // submission failing with "missing-input-response". So: render right
    // away when the form is already visible (the full-page Contact Us
    // section), but defer when it isn't — gp-contact-form-popup.js calls
    // ensureTurnstileRendered() once the popup is actually opened.
    if (!this.closest('[hidden]')) {
      this.initTurnstile();
    }
  };

  GpContactForm.prototype.disconnectedCallback = function () {
    if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit);
    if (this.fileInput && this._onFileChange) this.fileInput.removeEventListener('change', this._onFileChange);
    if (window.turnstile && this.turnstileWidgetId !== null) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (err) {
        /* widget already gone — nothing to do */
      }
    }
  };

  GpContactForm.prototype.initTurnstile = function () {
    var siteKey = this.dataset.turnstileSiteKey;
    if (!siteKey || !this.turnstileContainer) return;

    var self = this;
    loadTurnstile()
      .then(function (turnstile) {
        if (!self.isConnected) return;
        self.turnstileWidgetId = turnstile.render(self.turnstileContainer, {
          sitekey: siteKey,
          // Some browsers (seen in the wild: desktop Safari, when its
          // Private Access Token request to Cloudflare's challenge platform
          // gets an unexpected 401) fail the silent/invisible verification
          // attempt and would otherwise just sit there forever with no
          // token and no visible way for the visitor to recover. Without
          // this callback that shows up to the visitor as the widget
          // quietly doing nothing, and the submission fails with
          // "missing-input-response" only after they hit Send. Resetting
          // here makes Turnstile retry immediately, which in Managed mode
          // typically falls back to a visible checkbox instead of getting
          // stuck on the failed silent path.
          'error-callback': function () {
            self.resetTurnstile();
          },
          // A token is only valid for a few minutes — if someone leaves the
          // form open a while before submitting, refresh it quietly so
          // Send doesn't fail on an expired token.
          'expired-callback': function () {
            self.resetTurnstile();
          },
        });
      })
      .catch(function () {
        self.setStatus("Security check couldn't load. Please refresh the page and try again.", 'error');
      });
  };

  /**
   * Called by gp-contact-form-popup.js right after it un-hides the overlay.
   * Renders the widget for the first time if it hasn't been yet (deferred
   * from connectedCallback because the popup starts hidden — see above), or
   * asks for a fresh token if it was already rendered on an earlier open
   * (a token can go stale while the popup sat closed for a while).
   */
  GpContactForm.prototype.ensureTurnstileRendered = function () {
    if (this.turnstileWidgetId === null) {
      this.initTurnstile();
    } else {
      this.resetTurnstile();
    }
  };

  GpContactForm.prototype.resetTurnstile = function () {
    if (window.turnstile && this.turnstileWidgetId !== null) {
      try {
        window.turnstile.reset(this.turnstileWidgetId);
      } catch (err) {
        /* ignore */
      }
    }
  };

  GpContactForm.prototype.handleFileChange = function () {
    var files = Array.prototype.slice.call(this.fileInput.files || []);
    this.renderFileErrors(null);

    if (files.length > MAX_FILES) {
      this.setFieldError('photos', 'Please attach at most 6 photos.');
      files = files.slice(0, MAX_FILES);
      this.replaceFileInputFiles(files);
    }

    var total = 0;
    var invalid = [];
    var kept = [];
    files.forEach(function (file) {
      var ext = extensionOf(file.name);
      var reasons = [];
      if (ALLOWED_EXTENSIONS.indexOf(ext) === -1) reasons.push('unsupported type');
      if (file.size > MAX_FILE_SIZE) reasons.push('over 8 MB');
      total += file.size;
      if (reasons.length) {
        invalid.push({ file: file, reasons: reasons });
      } else {
        kept.push(file);
      }
    });

    if (total > MAX_TOTAL_SIZE) {
      this.setFieldError('photos', 'Total photo size must be 20 MB or smaller.');
    } else if (invalid.length === 0) {
      this.clearFieldError('photos');
    }

    this.renderFileList(kept, invalid);
    if (invalid.length) this.replaceFileInputFiles(kept);
  };

  GpContactForm.prototype.replaceFileInputFiles = function (files) {
    var dt = new DataTransfer();
    files.forEach(function (f) {
      dt.items.add(f);
    });
    this.fileInput.files = dt.files;
  };

  GpContactForm.prototype.renderFileList = function (kept, invalid) {
    if (!this.fileList) return;
    this.fileList.innerHTML = '';
    var self = this;

    kept.forEach(function (file) {
      var li = document.createElement('li');
      li.textContent = file.name + ' (' + formatSize(file.size) + ') ';
      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', 'Remove ' + file.name);
      removeBtn.textContent = '✕';
      removeBtn.addEventListener('click', function () {
        var remaining = Array.prototype.slice.call(self.fileInput.files || []).filter(function (f) {
          return f !== file;
        });
        self.replaceFileInputFiles(remaining);
        self.handleFileChange();
      });
      li.appendChild(removeBtn);
      self.fileList.appendChild(li);
    });

    invalid.forEach(function (entry) {
      var li = document.createElement('li');
      li.setAttribute('data-gp-file-error', 'true');
      li.textContent = entry.file.name + ' — ' + entry.reasons.join(', ');
      self.fileList.appendChild(li);
    });
  };

  GpContactForm.prototype.renderFileErrors = function () {
    // Reserved for future per-file inline messaging; field-level summary
    // is handled via setFieldError('photos', ...).
  };

  GpContactForm.prototype.setStatus = function (message, type) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      this.statusEl.removeAttribute('data-gp-status-type');
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    this.statusEl.setAttribute('data-gp-status-type', type || 'info');
    this.statusEl.focus && this.statusEl.setAttribute('tabindex', '-1');
  };

  GpContactForm.prototype.setFieldError = function (fieldName, message) {
    var input = this.form.querySelector('[data-gp-field="' + fieldName + '"]');
    var errorEl = this.form.querySelector('[data-gp-error-for="' + fieldName + '"]');
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.closest('.field') && input.closest('.field').classList.add('field--with-error');
    }
    if (errorEl) {
      errorEl.hidden = false;
      errorEl.textContent = message;
    } else if (fieldName === 'photos') {
      this.setStatus(message, 'error');
    }
  };

  GpContactForm.prototype.clearFieldError = function (fieldName) {
    var input = this.form.querySelector('[data-gp-field="' + fieldName + '"]');
    var errorEl = this.form.querySelector('[data-gp-error-for="' + fieldName + '"]');
    if (input) {
      input.removeAttribute('aria-invalid');
      input.closest('.field') && input.closest('.field').classList.remove('field--with-error');
    }
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  };

  GpContactForm.prototype.clearAllErrors = function () {
    var self = this;
    ['firstName', 'email', 'message', 'lastName', 'phone', 'orderNumber', 'photos'].forEach(function (f) {
      self.clearFieldError(f);
    });
    this.setStatus(null);
  };

  GpContactForm.prototype.validate = function () {
    var valid = true;
    var firstInvalid = null;
    var fields = {
      firstName: this.form.querySelector('[data-gp-field="firstName"]'),
      email: this.form.querySelector('[data-gp-field="email"]'),
      message: this.form.querySelector('[data-gp-field="message"]'),
    };

    if (!fields.firstName.value.trim()) {
      this.setFieldError('firstName', ERROR_MESSAGES.firstName.required);
      valid = false;
      firstInvalid = firstInvalid || fields.firstName;
    }

    var email = fields.email.value.trim();
    if (!email) {
      this.setFieldError('email', ERROR_MESSAGES.email.required);
      valid = false;
      firstInvalid = firstInvalid || fields.email;
    } else if (!EMAIL_RE.test(email)) {
      this.setFieldError('email', ERROR_MESSAGES.email.invalid_format);
      valid = false;
      firstInvalid = firstInvalid || fields.email;
    }

    if (!fields.message.value.trim()) {
      this.setFieldError('message', ERROR_MESSAGES.message.required);
      valid = false;
      firstInvalid = firstInvalid || fields.message;
    }

    if (this.fileInput && this.fileInput.files && this.fileInput.files.length > MAX_FILES) {
      this.setFieldError('photos', 'Please attach at most 6 photos.');
      valid = false;
    }

    if (firstInvalid) firstInvalid.focus();
    return valid;
  };

  GpContactForm.prototype.applyServerFieldErrors = function (fieldErrors) {
    var self = this;
    var first = null;
    Object.keys(fieldErrors).forEach(function (field) {
      var code = fieldErrors[field];
      var messages = ERROR_MESSAGES[field] || {};
      var message = messages[code] || 'Please check this field.';
      self.setFieldError(field, message);
      if (!first) {
        first = self.form.querySelector('[data-gp-field="' + field + '"]');
      }
    });
    if (first) first.focus();
  };

  GpContactForm.prototype.handleSubmit = function (event) {
    event.preventDefault();
    if (this.submitting) return;

    this.clearAllErrors();
    if (!this.validate()) return;

    var endpoint = this.dataset.endpoint;
    if (!endpoint) {
      this.setStatus("This form isn't connected yet. Please contact us by email instead.", 'error');
      return;
    }

    this.submitting = true;
    this.form.setAttribute('data-gp-submitting', 'true');
    this.submitButton.disabled = true;

    var formData = new FormData(this.form);
    var self = this;

    fetch(endpoint, { method: 'POST', body: formData })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { response: response, data: data };
          });
      })
      .then(function (result) {
        var data = result.data || {};
        if (result.response.ok && data.ok) {
          self.onSuccess();
        } else {
          self.onError(data);
        }
      })
      .catch(function () {
        self.setStatus("We couldn't reach the server. Please check your connection and try again.", 'error');
        self.resetTurnstile();
      })
      .finally(function () {
        self.submitting = false;
        self.form.removeAttribute('data-gp-submitting');
        self.submitButton.disabled = false;
      });
  };

  GpContactForm.prototype.onSuccess = function () {
    this.form.setAttribute('data-gp-sent', 'true');
    this.submitButton.disabled = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      this.successEl.focus && this.successEl.setAttribute('tabindex', '-1');
    }
    this.setStatus(null);
  };

  GpContactForm.prototype.onError = function (data) {
    this.resetTurnstile();
    if (data && data.error === 'validation_failed' && data.fields) {
      this.applyServerFieldErrors(data.fields);
      this.setStatus('Please fix the highlighted fields and try again.', 'error');
      return;
    }
    var code = (data && data.error) || 'unknown';
    var message = GENERIC_ERROR_MESSAGES[code] || "Something went wrong sending your message. Please try again.";
    this.setStatus(message, 'error');
  };

  if (!customElements.get('gp-contact-form')) {
    customElements.define('gp-contact-form', GpContactForm);
  }
})();